import classNames from "classnames"
import { useRouter } from "next/router"
import { useCallback } from "react"

import { Analytics } from "@bounce/analytics"
import { useLocale } from "@bounce/i18n"

import { getAllOfCityPoiListItemId } from "./utils/getAllOfCityPoiListItemId"
import SafeLink from "@/components/Generic/SafeLink/SafeLink"
import { InteractionEvents } from "@/libs/analytics/events"
import type { CityData, RelatedPoi } from "@/libs/bounce/cities"
import { useCityUrl, usePoiUrl } from "@/libs/router"

export type PoiListItemProps = {
  poi: Pick<RelatedPoi, "id" | "slug" | "translatedName" | "directorySlug">
  city: Pick<CityData, "slug" | "directorySlug">
}

export const PoiListItem = ({ poi, city }: PoiListItemProps) => {
  const router = useRouter()
  const { language } = useLocale()
  const { getUrl: getCityUrl } = useCityUrl({ city, directory: "luggage-storage" })
  // @TODO: Add directory to usePoiUrl when available
  const { getUrl: getPoiUrl } = usePoiUrl({ city, poi })

  const isAllOfCityItem = poi.id === getAllOfCityPoiListItemId(city)
  const isSelected = isAllOfCityItem
    ? router.query.city === city.slug && !router.query.place
    : router.query.place === poi.slug
  const url = isAllOfCityItem ? getCityUrl({ language, absolute: false }) : getPoiUrl({ language, absolute: false })

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()
      Analytics.track(InteractionEvents.LuggageStoragePoiClicked, {
        poiSlug: poi.slug,
        citySlug: city.slug,
        selection: isAllOfCityItem ? "all-of-city" : "poi",
      })

      void router.push(url)
    },
    [url, router, city.slug, poi.slug, isAllOfCityItem],
  )

  return (
    <li key={poi.id}>
      <SafeLink
        href={url}
        onClick={onClick}
        className={classNames(
          "block cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium text-neutral-800 transition-colors",
          {
            ["text-primary-700 border-primary-700 bg-primary-200"]: isSelected,
          },
        )}>
        {poi.translatedName}
      </SafeLink>
    </li>
  )
}
